import React, { useEffect } from "react";
import "./AppDialog.css";

function DeleteDialogIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M16.6666 18.3333V28.3333"
        stroke="currentColor"
        strokeWidth="2.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M23.3334 18.3333V28.3333"
        stroke="currentColor"
        strokeWidth="2.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M31.6667 9.99997V33.3333C31.6667 34.2174 31.3155 35.0652 30.6904 35.6903C30.0653 36.3154 29.2174 36.6666 28.3334 36.6666H11.6667C10.7827 36.6666 9.93481 36.3154 9.30968 35.6903C8.68456 35.0652 8.33337 34.2174 8.33337 33.3333V9.99997"
        stroke="currentColor"
        strokeWidth="2.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 9.99997H35"
        stroke="currentColor"
        strokeWidth="2.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.3334 10V6.66668C13.3334 5.78262 13.6846 4.93478 14.3097 4.30965C14.9348 3.68453 15.7827 3.33334 16.6667 3.33334H23.3334C24.2174 3.33334 25.0653 3.68453 25.6904 4.30965C26.3155 4.93478 26.6667 5.78262 26.6667 6.66668V10"
        stroke="currentColor"
        strokeWidth="2.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InfoDialogIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M20 36.6667C29.2048 36.6667 36.6667 29.2048 36.6667 20C36.6667 10.7953 29.2048 3.33334 20 3.33334C10.7953 3.33334 3.33337 10.7953 3.33337 20C3.33337 29.2048 10.7953 36.6667 20 36.6667Z"
        stroke="currentColor"
        strokeWidth="3.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20 13.3333V20"
        stroke="currentColor"
        strokeWidth="3.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20 26.6667H20.0167"
        stroke="currentColor"
        strokeWidth="3.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function AppDialog({
  open,
  type = "alert", // "alert" | "confirm" | "prompt"
  variant = "info", // "info" | "danger" | "error"
  icon = "alert", // "alert" | "delete"
  appName = "sama-tech.ae",
  title,
  message,
  inputLabel = "",
  inputPlaceholder = "",
  inputType = "text",
  inputValue = "",
  confirmText = "Ok",
  cancelText = "Cancel",
  showCancel = false,
  onConfirm,
  onCancel,
  onClose,
  onInputChange,
}) {
  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "Enter" && type !== "prompt") onConfirm?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, onConfirm, type]);

  if (!open) return null;

  const danger = variant === "danger" || variant === "error";
  const dialogText = `${title || ""} ${message || ""}`.toLowerCase();
  const successLike =
    variant === "success" ||
    (variant === "info" &&
      /\b(success|successful|successfully|deleted|completed|done)\b/.test(dialogText));
  const tone = icon === "delete"
    ? "delete"
    : successLike
      ? "success"
      : danger
        ? "error"
        : "info";

  const Icon = icon === "delete" ? DeleteDialogIcon : InfoDialogIcon;

  const cardVars = {
    "--app-dialog-icon-stroke": `var(--dialog-icon-stroke-${tone})`,
    "--app-dialog-halo-bg": `var(--dialog-halo-bg-${tone}, var(--dialog-halo-bg))`,
    "--app-dialog-ring-a": `var(--dialog-ring-a-${tone}, var(--dialog-ring-a))`,
    "--app-dialog-ring-b": `var(--dialog-ring-b-${tone}, var(--dialog-ring-b))`,
    "--app-dialog-wrap-glow-a": `var(--dialog-wrap-glow-a-${tone}, var(--dialog-wrap-glow-a))`,
    "--app-dialog-wrap-glow-b": `var(--dialog-wrap-glow-b-${tone}, var(--dialog-wrap-glow-b))`,
    "--app-dialog-icon-inset": `var(--dialog-icon-inset-${tone}, var(--dialog-icon-inset))`,
    "--app-dialog-icon-before-a": `var(--dialog-icon-before-a-${tone}, rgba(255, 70, 103, 0.55))`,
    "--app-dialog-icon-before-b": `var(--dialog-icon-before-b-${tone}, rgba(255, 70, 103, 0.2))`,
  };

  return (
    <div className="appDialogOverlay" onMouseDown={onClose}>
      <div
        className="appDialogCard"
        style={cardVars}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button
          className="appDialogClose"
          onClick={onClose}
          aria-label="Close dialog"
        >
          ×
        </button>

        <div className="appDialogBody">
          <div className="appDialogIconWrap">
            <div className="appDialogRing" />
            <div className="appDialogIcon">
              <span className="appDialogIconInk">
                <Icon />
              </span>
            </div>
          </div>

          <div className="appDialogText">
            <div className="appDialogAppName">{appName} says:</div>
            <div className="appDialogTitle">{title}</div>
            <div className="appDialogMessage">{message}</div>
            {type === "prompt" && (
              <label className="appDialogField">
                {inputLabel && <span className="appDialogFieldLabel">{inputLabel}</span>}
                <input
                  className="appDialogInput"
                  type={inputType}
                  value={inputValue}
                  placeholder={inputPlaceholder}
                  autoFocus
                  onChange={(e) => onInputChange?.(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onConfirm?.();
                    }
                  }}
                />
              </label>
            )}
          </div>
        </div>

        <div className="appDialogFooter">
          {(type === "confirm" || type === "prompt") && showCancel && (
            <button className="appDialogBtn appDialogBtnGhost" onClick={onCancel}>
              {cancelText}
            </button>
          )}

          <button
            className={
              "appDialogBtn " +
              (danger && (type === "confirm" || type === "prompt")
                ? "appDialogBtnDanger"
                : "appDialogBtnPrimary")
            }
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
